"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PostcodeTerritoriesSchema = void 0;
exports.isTerritoryRoutingEnabled = isTerritoryRoutingEnabled;
exports.resolvePostcodeOffice = resolvePostcodeOffice;
const zod_1 = require("zod");
/** Exact postcode ownership; map geometry is presentation only. */
exports.PostcodeTerritoriesSchema = zod_1.z.array(zod_1.z.object({
    officeId: zod_1.z.string().min(1).max(120).regex(/^[^/]+$/),
    postcodes: zod_1.z.array(zod_1.z.string().regex(/^\d{4}$/).refine((p) => Number(p) >= 200, 'Enter an Australian postcode.')).min(1).max(10000),
})).max(100).superRefine((territories, ctx) => {
    if (territories.reduce((n, t) => n + t.postcodes.length, 0) > 10000) {
        ctx.addIssue({ code: 'custom', message: 'A routing configuration can contain at most 10,000 postcodes.' });
        return;
    }
    const offices = new Set();
    const owners = new Set();
    for (const [index, territory] of territories.entries()) {
        if (offices.has(territory.officeId)) {
            ctx.addIssue({ code: 'custom', path: [index, 'officeId'], message: 'Use one territory per office.' });
        }
        offices.add(territory.officeId);
        for (const postcode of territory.postcodes) {
            if (owners.has(postcode)) {
                ctx.addIssue({ code: 'custom', path: [index, 'postcodes'], message: `Postcode ${postcode} has more than one owner.` });
            }
            owners.add(postcode);
        }
    }
});
/**
 * Single source of truth for "is this tenant routing by postcode territory?".
 * The router, the save handler and the config page MUST all use this so they
 * cannot drift.
 *
 * An explicit `territoryRoutingEnabled` wins outright. Only when the tenant has
 * never set it do we fall back to the pre-flag behaviour, and even then an
 * EMPTY territory list must not activate territory mode: the config page used
 * to send `postcodeTerritories: []` on every save, which silently put every
 * tenant into territory mode where an uncovered postcode returns unassigned.
 */
function isTerritoryRoutingEnabled(rules) {
    if (typeof rules.territoryRoutingEnabled === 'boolean') {
        return rules.territoryRoutingEnabled;
    }
    return (rules.postcodeTerritories?.length ?? 0) > 0;
}
/** Input is the canonical four-digit postcode (including leading zeros). */
function resolvePostcodeOffice(postcode, territories, fallbackOfficeId) {
    return territories.find((t) => postcode !== null && t.postcodes.includes(postcode))?.officeId
        ?? fallbackOfficeId ?? null;
}
